<template>
	<div
		id="app"
		:class="['site', navReady && 'site--contained']"
		:mode="activeMode"
		:style="brandingVars"
	>
		<VueUiChat
			v-if="navReady"
			:displayOnActiveFeatureSwitches="[
				FEATURE_SWITCHES.AI_POWERED_INTERFACE
			]"
		/>
		<VueUiHeader :reponsibleForUserInfo="false" v-if="navReady" />
		<hr class="horizontal-div" v-if="navReady" />
		<VueUiMenu :reponsibleForUserInfo="false" v-if="navReady" />
		<VueUiTitle
			@versionChange="onVersionChange"
			@versionChangeStart="onVersionChangeStart"
			:pageVersion="2"
			:setPageVersion="true"
			v-if="navReady"
		/>
		<VueConcurrentUsers
			v-if="displayConcurrentUsers"
			class="active-users--concurrent-layout"
			ref="concurrentUsersRef"
		/>
		<VueUiAlerts v-if="appReady" :showingNavigation="showNavigation" />
		<VueUiWarning />
		<!-- This zIndex value corresponds with $z-level6: 12000; -->
		<OverlayBackdrop v-if="appPageLoading" :fullPage="true" :zIndex="12000">
			<Loader class="site__app-loading" variant="squareExploder" />
		</OverlayBackdrop>
		<main :class="['site__content', hasActionRibbon && 'has-action-bar']">
			<ModeToggle v-if="showModeToggle" />
			<ErrorNotification />
			<router-view v-if="showRouterView" />
		</main>
		<iframe
			:src="messagePageWithCid"
			style="visibility: hidden; position: absolute; top: -9999px; left: -9999px;"
			height="0"
			width="0"
			v-if="messagePageWithCid"
			ref="messageFrame"
		/>
		<AccessPageViewer />
		<DesktopOptimized />
		<div id="footermarker"><!-- hs div --></div>
		<div :class="[showNavigation && 'site__toast-container', !showNavigation && 'site__toast-container-iframe']">
			<portal-target name="toast" multiple class="toasts" tag="ul" />
		</div>
		<portal-target name="popover" />
		<portal-target name="chat" class="vue-chat" />
		<portal-target name="modal" multiple />
		<PrintQueueMultipleIframe />
	</div>
</template>

<script>
// @ is an alias to /src
import ModeToggle from '@/site-components/navigation/mode-toggle.vue';
import { mapGetters, mapActions } from 'vuex';
import gotoPageVersion from '@/utils/goto-page-version';
import OverlayBackdrop from '@/components/visual/overlay-backdrop/overlay-backdrop.vue';
import Loader from '@/components/visual/loader/loader.vue';
import messageHandlerMixin from '@/mixins/message-handler-mixin';
import AccessPageViewer from '@/site-components/integration/access-page-viewer.vue';
import userLoadedMixin from '@/mixins/user-loaded-mixin';
import DesktopOptimized from '@/components/notifications/desktop-optimized/desktop-optimized-notification.vue';
import ErrorNotification from '@/site-components/error-notification/error-notification.vue';
import PrintQueueMultipleIframe from '@/site-components/printing/print-queue-multiple-iframe.vue';
import VueUiHeader from '@/navigation/vue-ui-header.vue';
import VueUiMenu from '@/navigation/vue-ui-menu.vue';
import VueUiAlerts from '@/navigation/vue-alerts.vue';
import VueUiTitle from '@/navigation/vue-ui-title.vue';
import VueUiWarning from '@/navigation/vue-warning.vue';
import VueConcurrentUsers from '@/navigation/vue-concurrent-users.vue';
import consoleLog from '@/utils/console-log';
import VueUiChat from '@/navigation/vue-ui-chat.vue';
import { FEATURE_SWITCHES } from '@/constants/feature-switches';

export default {
	name: 'App',
	metaInfo: {
		// all titles will be injected into this template
		//used by vue-meta which is registerd in main.js
		titleTemplate: '%s | Dominion Vue'
	},
	mixins: [messageHandlerMixin, userLoadedMixin],
	components: {
		ModeToggle,
		OverlayBackdrop,
		Loader,
		AccessPageViewer,
		DesktopOptimized,
		ErrorNotification,
		PrintQueueMultipleIframe,
		VueUiHeader,
		VueUiMenu,
		VueUiAlerts,
		VueUiTitle,
		VueUiWarning,
		VueConcurrentUsers,
		VueUiChat
	},
	data() {
		return {
			showModeToggleConfig: this.VUE_APP_MODE_TOGGLE,
			showVersionSwitch: false,
			messagePage: '/account/vuemessage',
			reloadIframeSeconds: 60 * 5,
			loggingOut: false,
			FEATURE_SWITCHES: FEATURE_SWITCHES
		};
	},
	computed: {
		...mapGetters('app', [
			'appPageLoading',
			'hasActionRibbon',
			'VUE_APP_MODE_TOGGLE',
			'VUE_APP_LOGGING_SOURCE_APPLICATION',
			'VUE_APP_CLIENT_LOGGING_ENABLED',
			'VUE_APP_CLIENT_LOGGING_API',
			'VUE_APP_CLIENT_ENVIRONMENT_REDIRECT_ENABLED'
		]),
		...mapGetters('branding', ['brandingVars', 'activeMode']),
		...mapGetters('principal', [
			'user',
			'accessToken',
			'principalReady',
			'employeePositions',
			'employeeFullName',
			'doLogout'
		]),
		...mapGetters('communicationHub', [
			'hubReady',
			'displayConcurrentUsers'
		]),
		...mapGetters('user', ['currentCompanyId', 'userInfo', 'userInfoSlim']),
		showModeToggle() {
			return this.showModeToggleConfig && this.isUserLoaded;
		},
		navReady() {
			let result = false;

			if (this.appReady && this.showNavigation) {
				result = true;
			}

			return result;
		},
		appReady() {
			let result = false;

			if (this.currentCompanyId > 0 && this.isUserLoaded) {
				result = true;
			}

			return result;
		},
		messagePageWithCid() {
			let cid = this.currentCompanyId;
			let url;

			if (this.principalReady && cid && cid > 0) {
				url = `${this.messagePage}?cid=${cid}`;
			} else if (this.doLogout) {
				url = `${this.messagePage}`;
			}

			//should return null if the principal isn't ready so the iframe doesn't show
			return url;
		},
		showRouterView() {
			let result = true;

			if (this.loggingOut || this.doLogout) {
				result = false;
			}

			return result;
		},
		publicRoute() {
			let result = false;

			//checking on route.name because an empty route / is always triggered first for some reason
			if (
				!this.$route.name ||
				(this.$route.meta && this.$route.meta.isPublic)
			) {
				result = true;
			}

			return result;
		},
		showNavigation() {
			let result = true;
			const meta = this.$route.meta;
			let hide = false;

			if (meta) {
				hide = meta.hideNavigation;

				if (hide) {
					result = false;
				}
			}

			return result;
		}
	},
	methods: {
		...mapActions('app', ['setPageLoadingAction']),
		...mapActions('user', [
			'setUserInfoAction',
			'setTokenAction',
			'logoutUserAction'
		]),
		...mapActions('principal', [
			'signOut',
			'getUser',
			'removeUser',
			'silentRenew',
			'setPrincipalTokenAction',
			'clearPrincipalAction'
		]),
		...mapActions('hub', ['setHubConnectionAction']),
		...mapActions('logging', ['initLoggingAction']),
		async setUserLoaded() {
			this.setUserInfoAction(this.user);
			this.redirectEnvironmentSubdomain(this.user);
			this.setTokenAction(this.accessToken);
			this.setHubConnectionAction({
				userToken: this.accessToken,
				user: this.userInfoSlim
			});
			this.initLoggingAction({
				loggingEnabled: this.VUE_APP_CLIENT_LOGGING_ENABLED,
				userToken: this.accessToken,
				sourceApplication: this.VUE_APP_LOGGING_SOURCE_APPLICATION,
				loggingApi: this.VUE_APP_CLIENT_LOGGING_API
			});
		},
		logout() {
			this.loggingOut = true;
			//this.signOutOidc();
			this.signOut();
			this.clearPrincipalAction();

			//trigger logout in requesttoken iframe
			const iframe = this.$refs.messageFrame;

			if (
				iframe &&
				iframe.contentWindow &&
				iframe.contentWindow.DoLogout &&
				typeof iframe.contentWindow.DoLogout === 'function'
			) {
				iframe.contentWindow.DoLogout();
			}
		},
		onVersionChangeStart() {
			this.setPageLoadingAction(true);
		},
		onVersionChange() {
			//remove the user so it will reload
			//when we come back to vue-ui
			this.removeUser();

			//goto the v1 url
			gotoPageVersion(this.$route);
		},
		onSilentRenew() {
			this.silentRenew();
		},
		/**
		 * Called from the message handler mixin when a message is emitted from the AccessWeb
		 * /account/vuemessage iframe.
		 */
		onMessage(message) {
			const reloadMills = this.reloadIframeSeconds * 1000;

			if (message) {
				if (message.VueApiToken) {
					this.setPrincipalTokenAction(message.VueApiToken);
					this.setTokenAction(message.VueApiToken);
					consoleLog(
						'onMessage: VueApiToken',
						message.VueApiToken.slice(-10)
					);
				}

				if (message.RequestVerificationToken || message.VueApiToken) {
					window.setTimeout(this.reoloadIframe, reloadMills);
				}

				if (message.action && message.action === 'noauth') {
					this.logoutUserAction();
					this.clearPrincipalAction();
				}
			}
		},
		reoloadIframe() {
			const frameRef = this.$refs.messageFrame;

			if (frameRef) {
				frameRef.contentWindow.location.reload();
			}
		},
		redirectEnvironmentSubdomain(user) {
			if (
				user &&
				user.EnvironmentSubdomain &&
				this.VUE_APP_CLIENT_ENVIRONMENT_REDIRECT_ENABLED
			) {
				let targetSubdomain = user.EnvironmentSubdomain;
				let currentSubdomain = window.location.hostname.split('.')[0];
				if (
					currentSubdomain.localeCompare(targetSubdomain, undefined, {
						sensitivity: 'base'
					}) !== 0
				) {
					window.location.href = window.location.href.replace(
						currentSubdomain,
						targetSubdomain
					);
				}
			}
		}
	},
	watch: {
		principalReady: {
			inmediate: true,
			handler(val) {
				if (val && this.$gtm.enabled()) {
					var datalayer = window.dataLayer || [];
					datalayer.push({
						globalDealershipGroupID: this.user.AspNetCompaniesId,
						globalDealershipGroup: this.user.DealershipGroupNumber,
						globalCompanyID: this.user.CurrentEmployeeCompanyId,
						globalCompanyName: this.user.CurrentCompanyName,
						globalCompanyOEM: this.user.CompanyOEMs,
						globalEmployeeID: this.user.EmployeeId,
						globalEmployeeName: this.employeeFullName,
						globalEmployeeRole: this.user.PositionNames
					});
				}
			}
		}
	},
	mounted() {
		window.addEventListener('principal:userLoaded', this.setUserLoaded);
		window.addEventListener('vuelogout', this.logout);
	},
	destroyed() {
		window.removeEventListener('principal:userLoaded', this.setUserLoaded);
		window.removeEventListener('vuelogout', this.logout);

		this.setPageLoadingAction(false);
	}
};
</script>

<style lang="scss">
/**
			* Application Page
			* Can use SCSS variables = TRUE
	*/
@import '@/scss';

.vue-title ~ .site__content {
	position: relative;
	margin-top: 86px;
}

.site {
	font-family: 'Lato', sans-serif;
	margin: 0 auto;
	background: var(--bg-0);
	color: var(--bg-0-fg-4);
}

// Site pages that are contained and accounts for side site nav
.site--contained {
	position: relative; //fix horizontal scroll bug
	max-width: 1402px; //same max-width as accessWeb

	.site__content {
		display: flex;
		flex-direction: column;
		height: calc(100vh - 86px);
		z-index: $z-level1;

		@include mq($from: md-l) {
			padding-left: 47px; //width of the side vertical navigation
		}

		&.has-action-bar > div[class$='-page'] {
			margin-top: 70px; //height of the Action Ribbon
		}
	}

	.toasts {
		top: -39px; // amount needed to overlap the Page Title
		padding-right: 0;
	}
}
.site__app-loading.loader--square-exploder {
	@include transform-full-center;
	top: 30%;
}

/*------------------------------------*\
  #TOASTS
  Container for placement of toast notifications.
\*------------------------------------*/
.toasts {
	position: absolute;
	z-index: $z-level7;
	top: 0;
	right: 0;
	max-height: calc(100vh - 88px);
	overflow-y: auto;
	overflow-x: hidden; //Needed to prevent scrollbar on transition animation
	padding: $padding-sm;
	display: inline-flex;
	flex-direction: column;
	gap: $gutter-md;
	pointer-events: auto;
}

.site__toast-container-iframe {
	position: fixed;
	display: flex;
	justify-content: flex-end;
	max-height: calc(100vh - 88px);
	top: 56px;
	right: calc(((100% - 1402px) / 2) + 8px);
	width: 100%;
	z-index: $z-level7;
	pointer-events: auto;

	@include mq($until: 1402) {
		right: $gutter-md;
	}
}

#app .active-users--concurrent-layout {
	top: -40px;
}
</style>
